/**
 * Shared anatomy of the ID / Income / Rent verification modals (design
 * §13/§14 + the ID export). Income & Rent redesign (Figma) share
 * VerificationRedesignShell; ID keeps VerificationModalShell.
 */
import {
	Check,
	ChevronDown,
	CircleUser,
	CreditCard,
	Download,
	Landmark,
	MoveLeft,
	MoveRight,
	X,
} from 'lucide-react'
import type { ReactNode } from 'react'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'
import type { VerificationDisclaimer } from '#app/utils/application-detail-v2/verification-disclaimers'
import {
	type AiDeclaredIncomePeriod,
	type ConnectedFlinksAccount,
	INCOME_CATEGORY_COLORS,
	INCOME_CATEGORY_PILL_STYLES,
	INCOME_FILTER_LABELS,
	type IncomeCategoryKey,
	type MonthBucket,
	RENT_FILTER_COLORS,
	RENT_FILTER_LABELS,
	RENT_MATCH_PILL_STYLES,
	RENT_STATUS_STYLES,
	type RentFilterKey,
	type RentMonthCell,
	formatConnectedDate,
	formatRentTxnDate,
	formatTxnAmount,
	formatTxnDate,
	formatTxnDay,
	formatWholeAmount,
	orderedIncomeCategories,
	orderedRentFilters,
	rentMatchKind,
	visibleRentMonthCells,
} from '#app/utils/application-detail-v2/verification-display'
import { cn } from '#app/utils/misc'
import type { VerificationDataItem } from '#app/utils/report-data-helpers'
import { IconSquare, type PastelTone } from '../primitives'

export function VerificationModalShell({
	open,
	onOpenChange,
	title,
	verified,
	children,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	verified: boolean
	children: ReactNode
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="block max-h-[88vh] gap-0 overflow-y-auto rounded-3xl border-[#ECECEA] bg-[#fbfdfd] p-6 sm:max-w-6xl sm:p-7">
				<DialogHeader className="flex-row items-center gap-3 space-y-0">
					<DialogTitle className="font-calgary text-[28px] leading-tight font-medium text-[#1F1E1D]">
						{title}
					</DialogTitle>
					{verified ? (
						<span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-600">
							Verified
						</span>
					) : null}
					<DialogDescription className="sr-only">{title}</DialogDescription>
				</DialogHeader>
				{children}
			</DialogContent>
		</Dialog>
	)
}

/** Income & Rent redesign shell — white modal; gray strip is owned by filters. */
export function VerificationRedesignShell({
	open,
	onOpenChange,
	title,
	accountHolder,
	showRequestBank,
	onRequestBank,
	canDownload,
	onDownload,
	downloadBusy,
	filters,
	children,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	accountHolder: string
	showRequestBank: boolean
	onRequestBank: () => void
	canDownload: boolean
	onDownload: () => void
	downloadBusy: boolean
	/** Filter strip rendered full-bleed under the white header. */
	filters?: ReactNode
	children: ReactNode
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="block max-h-[88vh] w-full gap-0 overflow-y-auto rounded-3xl border-[#ECECEA] bg-white p-0 sm:max-w-7xl"
			>
				<DialogHeader className="w-full flex-row px-3 md:px-5 pt-5 pb-4 sm:px-7 sm:pt-6 2xl:px-10 2xl:pt-8">
					<div className="w-full flex flax-wrap flex-col md:flex-row items-start md:items-center justify-between gap-3 space-y-0">
						<DialogTitle className="font-calgary text-[28px] leading-tight font-medium text-[#1F1E1D] 2xl:text-[40px]">
							{title}
						</DialogTitle>
						<DialogDescription className="sr-only">{title}</DialogDescription>
						<div className="flex  mr-[-25px] md:mr-0 flex-wrap items-center justify-end gap-1 md:gap-2 sm:gap-3">
							{canDownload ? (
								<button
									type="button"
									onClick={onDownload}
									disabled={downloadBusy}
									aria-label={
										downloadBusy ? 'Preparing download' : 'Download report'
									}
									className="flex size-7 md:size-10 cursor-pointer items-center justify-center rounded-full border border-[#32302F] bg-white text-[#32302F] transition-colors hover:bg-[#F0F0EE] disabled:cursor-not-allowed disabled:opacity-60 2xl:size-15"
								>
									<Download className="size-3.5 md:size-4 2xl:size-8" />
								</button>
							) : null}
							{showRequestBank ? (
								<button
									type="button"
									onClick={onRequestBank}
									className="flex h-7 md:h-10 cursor-pointer items-center gap-2 rounded-full bg-[#32302F] px-3 md:px-4 text-[14px] md:text-[18px] font-medium text-white transition-colors hover:bg-[#1a1918] 2xl:h-15 2xl:text-[23px]"
								>
									<CreditCard className="size-3.5 md:size-4 2xl:size-5" />
									Request Bank Account
								</button>
							) : null}
							{accountHolder ? (
								<div className="min-w-0 text-right">
									<p className="text-[10px] md:text-[11px] leading-tight text-[#747474] 2xl:text-[18px]">
										Account Holder
									</p>
									<p className="truncate text-[12px] md:text-[14px] font-medium text-[#32302F] 2xl:text-[24px]">
										{accountHolder}
									</p>
								</div>
							) : null}
						</div>
					</div>
					<DialogClose
						aria-label="Close"
						className="flex size-8 cursor-pointer items-center justify-center rounded-full text-[#000000] transition-colors hover:bg-[#EFEFED] hover:text-[#32302F]"
					>
						<X className="size-4" />
					</DialogClose>
				</DialogHeader>
				{filters}
				<div className="px-3 md:px-5 pt-3 md:pt-5 pb-6 sm:px-7 sm:pb-7">
					{children}
				</div>
			</DialogContent>
		</Dialog>
	)
}

/** @deprecated Use VerificationRedesignShell — kept as alias for income modal. */
export const IncomeModalShell = VerificationRedesignShell

function CategoryFilterButton({
	active,
	label,
	dotColor,
	onClick,
}: {
	active: boolean
	label: string
	dotColor?: string
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'flex h-7 md:h-9 cursor-pointer items-center gap-1 md:gap-2 rounded-full border px-2 md:px-3.5 text-[11px] md:text-[13px] transition-colors 2xl:h-11.5 2xl:gap-3 2xl:text-[16px]',
				active
					? 'border-[#32302F] bg-white text-[#32302F]'
					: 'border-[#D6D6D4] bg-transparent text-[#747474] hover:border-[#C9C9C7]',
			)}
		>
			{dotColor ? (
				<span
					className="size-1.5 md:size-2 shrink-0 rounded-full"
					style={{ backgroundColor: dotColor }}
					aria-hidden="true"
				/>
			) : null}
			{label}
			<span className="w-4 md:w-5.5">
				<span
					className={cn(
						'flex items-center justify-center rounded-full',
						active
							? 'size-3 md:size-4 bg-[#32302F] text-white'
							: 'size-3.5 md:size-5.5 border border-[#C9C9C7] bg-transparent',
					)}
				>
					{active ? (
						<Check className="size-2 md:size-2.5" strokeWidth={3} />
					) : null}
				</span>
			</span>
		</button>
	)
}

export function IncomeCategoryFilters({
	present,
	selected,
	onChange,
}: {
	present: IncomeCategoryKey[]
	selected: IncomeCategoryKey[]
	onChange: (next: IncomeCategoryKey[]) => void
}) {
	if (present.length === 0) return null
	const allSelected =
		present.length > 0 && present.every((key) => selected.includes(key))

	const toggle = (key: IncomeCategoryKey) => {
		if (selected.includes(key)) {
			onChange(orderedIncomeCategories(selected.filter((k) => k !== key)))
		} else {
			onChange(orderedIncomeCategories([...selected, key]))
		}
	}

	return (
		<div className="flex flex-wrap items-center gap-2 bg-[#F4F6F6] px-5 py-3 sm:px-7 2xl:mt-3">
			<CategoryFilterButton
				active={allSelected}
				label="All"
				dotColor={undefined}
				onClick={() =>
					onChange(allSelected ? [] : orderedIncomeCategories(present))
				}
			/>
			{present.map((key) => {
				const active = selected.includes(key)
				return (
					<CategoryFilterButton
						key={key}
						active={active}
						label={INCOME_FILTER_LABELS[key]}
						dotColor={INCOME_CATEGORY_COLORS[key]}
						onClick={() => toggle(key)}
					/>
				)
			})}
		</div>
	)
}

export function RentCategoryFilters({
	present,
	selected,
	onChange,
}: {
	present: RentFilterKey[]
	selected: RentFilterKey[]
	onChange: (next: RentFilterKey[]) => void
}) {
	if (present.length === 0) return null
	const allSelected =
		present.length > 0 && present.every((key) => selected.includes(key))

	const toggle = (key: RentFilterKey) => {
		if (selected.includes(key)) {
			onChange(orderedRentFilters(selected.filter((k) => k !== key)))
		} else {
			onChange(orderedRentFilters([...selected, key]))
		}
	}

	return (
		<div className="flex flex-wrap items-center gap-2 bg-[#F4F6F6] px-5 py-3 sm:px-7 2xl:mt-8">
			<CategoryFilterButton
				active={allSelected}
				label="All"
				dotColor={undefined}
				onClick={() => onChange(allSelected ? [] : orderedRentFilters(present))}
			/>
			{present.map((key) => {
				const active = selected.includes(key)
				return (
					<CategoryFilterButton
						key={key}
						active={active}
						label={RENT_FILTER_LABELS[key]}
						dotColor={RENT_FILTER_COLORS[key]}
						onClick={() => toggle(key)}
					/>
				)
			})}
		</div>
	)
}

export function RentMatchPill({
	category,
}: {
	category: string | null | undefined
}) {
	const kind = rentMatchKind(category)
	const style = kind ? RENT_MATCH_PILL_STYLES[kind] : null
	if (!style) {
		return (
			<span className="inline-flex shrink-0 items-center rounded-full bg-[#F4F6F6] px-2.5 py-0.5 text-[11px] font-medium text-[#747474] 2xl:text-[14px]">
				{category?.replace(/_/g, ' ') || '-'}
			</span>
		)
	}
	return (
		<span
			className="inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium 2xl:text-[14px]"
			style={{ backgroundColor: style.bg, color: style.text }}
		>
			{style.label}
		</span>
	)
}

export function RentSummarySection({
	declaredRent,
	previousDeclaredRent,
	months,
}: {
	declaredRent: number | null
	previousDeclaredRent?: number | null
	months: RentMonthCell[]
}) {
	const visibleMonths = visibleRentMonthCells(months)

	return (
		<section className="rounded-2xl border border-[#EFEFED] bg-white p-4 sm:p-5">
			<div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-[#747474] 2xl:text-[14px]">
				<span className="flex items-center gap-2">
					<span
						className="size-2 rounded-full"
						style={{ backgroundColor: RENT_STATUS_STYLES.strong.dot }}
					/>
					Strong Match
				</span>
				<span className="flex items-center gap-2">
					<span
						className="size-2 rounded-full"
						style={{ backgroundColor: RENT_STATUS_STYLES.possible.dot }}
					/>
					Possible Match
				</span>
				<span className="flex items-center gap-2">
					<span
						className="size-2 rounded-full"
						style={{ backgroundColor: RENT_STATUS_STYLES.none.dot }}
					/>
					No Payment Found
				</span>
			</div>

			<div className="mt-4 flex flex-wrap items-center gap-3 2xl:gap-4">
				<DeclaredRentChip label="Current Declared Rent" amount={declaredRent} />
				{previousDeclaredRent != null ? (
					<DeclaredRentChip
						label="Previous Declared Rent"
						amount={previousDeclaredRent}
					/>
				) : null}
			</div>

			{visibleMonths.length > 0 ? (
				<div className="mt-4 grid grid-cols-3 gap-2.5 sm:grid-cols-6 2xl:mt-5 2xl:gap-3">
					{visibleMonths.map((month) => {
						const palette = RENT_STATUS_STYLES[month.status]
						return (
							<div
								key={month.key}
								className="flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-3 text-center 2xl:py-6"
								style={{ backgroundColor: palette.bg, color: palette.text }}
							>
								<span className="w-full text-center text-[11px] leading-none font-medium whitespace-nowrap 2xl:text-[16px]">
									{month.shortLabel}
								</span>
								<span className="w-full text-center text-[14px] leading-none font-semibold whitespace-nowrap tabular-nums 2xl:text-[20px]">
									{formatWholeAmount(month.amount)}
								</span>
							</div>
						)
					})}
				</div>
			) : null}
		</section>
	)
}

function DeclaredRentChip({
	label,
	amount,
}: {
	label: string
	amount: number | null
}) {
	return (
		<div className="flex items-center gap-3 rounded-2xl border border-[#F0F0F0] bg-[#F8F8FC] px-3.5 py-3 2xl:gap-4 2xl:px-5 2xl:py-4">
			<span className="flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-[#efeaf6] text-[#9976B8] 2xl:size-11">
				<CircleUser className="size-4.5 2xl:size-6" strokeWidth={1.75} />
			</span>
			<div className="min-w-0">
				<p className="text-[12px] text-[#747474] 2xl:text-[18px]">{label}</p>
				<p className="text-[16px] leading-tight font-semibold whitespace-nowrap text-[#32302F] 2xl:text-[24px]">
					{formatWholeAmount(amount)}
					<span className="ml-1 text-[12px] font-normal text-[#747474] 2xl:text-[14px]">
						/month
					</span>
				</p>
			</div>
		</div>
	)
}

export function RentTransactionTable({
	items,
	totalCount,
}: {
	items: VerificationDataItem[]
	/** Unfiltered count, for "13 Of 24 Transactions". Defaults to `items`. */
	totalCount?: number
}) {
	const total = totalCount ?? items.length
	if (items.length === 0) {
		return (
			<p className="mt-4 text-sm text-[#747474]">
				No rent-related transactions were identified for this applicant.
			</p>
		)
	}
	return (
		<section className="mt-5 overflow-hidden rounded-2xl border border-[#EFEFED] bg-white">
			<div className="flex items-center justify-between gap-3 px-4 py-3 2xl:px-5">
				<h3 className="text-[15px] font-semibold text-[#32302F] 2xl:text-[18px]">
					Rent Transactions
				</h3>
				<span className="shrink-0 text-[12px] text-[#747474] 2xl:text-[14px]">
					{items.length} Of {total} Transactions
				</span>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[640px] border-collapse text-left">
					<thead>
						<tr className="bg-[#F4F6F6] text-[13px] font-medium text-[#32302F] 2xl:text-[15px]">
							<th className="w-0 px-4 py-2.5 font-medium whitespace-nowrap 2xl:px-5 2xl:py-3">
								Date
							</th>
							<th className="w-full px-4 py-2.5 font-medium 2xl:px-5 2xl:py-3">
								Description
							</th>
							<th className="w-0 px-6 py-2.5 text-center font-medium whitespace-nowrap 2xl:px-5 2xl:py-3">
								Match
							</th>
							<th className="w-0 px-4 py-2.5 text-right font-medium whitespace-nowrap 2xl:px-5 2xl:py-3">
								Amount
							</th>
						</tr>
					</thead>
					<tbody>
						{items.map((item, index) => (
							<tr
								key={`${item.date}-${index}`}
								className={cn(
									'text-[12px] text-[#32302F] 2xl:text-[14px]',
									index > 0 && 'border-t border-dashed border-[#E8E8E6]',
								)}
							>
								<td className="w-0 px-4 py-3 whitespace-nowrap text-[#32302F] 2xl:px-5 2xl:py-3.5">
									{formatRentTxnDate(item.date)}
								</td>
								<td className="w-full min-w-0 px-4 py-3 text-[#747474] 2xl:px-5 2xl:py-3.5">
									{item.description || '-'}
								</td>
								<td className="w-0 px-6 py-3 whitespace-nowrap 2xl:px-5 2xl:py-3.5">
									<RentMatchPill category={item.category} />
								</td>
								<td className="w-0 px-4 py-3 text-right font-semibold whitespace-nowrap text-[#32302F] tabular-nums 2xl:px-5 2xl:py-3.5">
									{formatTxnAmount(item.amount)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	)
}

function DeclaredAmountCell({
	caption,
	amount,
}: {
	caption: string
	amount: number | null
}) {
	return (
		<div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 pr-4 2xl:gap-4 2xl:pr-6">
			<span className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-[#efeaf6] text-[#9976B8] 2xl:size-12">
				<CircleUser className="size-5 2xl:size-6" strokeWidth={1.75} />
			</span>
			<div className="min-w-0">
				<p className="text-[12px] text-[#747474] 2xl:text-[16px]">{caption}</p>
				<p className="text-[18px] leading-tight font-medium whitespace-nowrap text-[#32302F] 2xl:text-[22px]">
					{formatWholeAmount(amount)}
					<span className="ml-1 text-[13px] font-normal text-[#747474] 2xl:text-[14px]">
						/month
					</span>
				</p>
			</div>
		</div>
	)
}

function DeclaredPeriodRow({
	showLabel,
	label,
	period,
}: {
	showLabel: boolean
	label: string
	period: AiDeclaredIncomePeriod | null
}) {
	return (
		<div className="flex flex-col flex-wrap items-center gap-3 sm:flex-row sm:gap-4">
			{showLabel ? (
				<span className="w-16 shrink-0 text-[13px] text-[#747474] 2xl:text-[18px]">
					{label}
				</span>
			) : null}
			<div className="flex min-w-0 flex-1 flex-col items-stretch overflow-hidden rounded-2xl border border-[#F0F0F0] bg-[#EFEAF64D] sm:flex-row">
				<DeclaredAmountCell
					caption="Gross"
					amount={period?.grossMonthly ?? null}
				/>
				<div
					aria-hidden="true"
					className="my-3 w-px shrink-0 self-stretch bg-[#F0F0F0]"
				/>
				<DeclaredAmountCell caption="Net" amount={period?.netMonthly ?? null} />
			</div>
		</div>
	)
}

export function IncomeComparisonSummary({
	current,
	previous,
	showPrevious,
	salaryCaption,
	averageMonthly,
}: {
	current: AiDeclaredIncomePeriod | null
	previous: AiDeclaredIncomePeriod | null
	/** When true, always render the Previous row (null → '-'). */
	showPrevious: boolean
	salaryCaption: string | null
	averageMonthly: number | null
}) {
	return (
		<div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-[#EFEFED] bg-white p-5 sm:gap-4 xl:gap-5 md:flex-row">
			<div className="flex w-full flex-col items-center justify-between md:w-3/5 md:flex-row">
				<div className="">
					<p className="text-[14px] font-medium text-[#32302F] 2xl:text-[20px]">
						What They Declared
					</p>
					<div className="mt-3 flex flex-col gap-1.5 sm:gap-2.5">
						<DeclaredPeriodRow
							showLabel={showPrevious}
							label="Current"
							period={current}
						/>
						{showPrevious ? (
							<DeclaredPeriodRow
								showLabel={true}
								label="Previous"
								period={previous}
							/>
						) : null}
					</div>
					{salaryCaption ? (
						<p className="mt-3 text-[12px] text-[#747474] 2xl:text-[16px]">
							{salaryCaption}
						</p>
					) : null}
				</div>
				<div
					aria-hidden="true"
					className="hidden flex-col items-center gap-0.5 text-[#32302F] md:flex"
				>
					<MoveLeft className="h-5 w-14 2xl:h-6 2xl:w-20" strokeWidth={1.75} />
					<MoveRight className="h-5 w-14 2xl:h-6 2xl:w-20" strokeWidth={1.75} />
					<span className="mt-1 text-[14px] font-medium text-[#747474] 2xl:text-[18px]">
						vs
					</span>
				</div>
			</div>
			<div className="items-start sm:justify-self-end sm:text-right">
				<p className="pb-3 text-[14px] font-medium text-[#32302F] 2xl:pb-4 2xl:text-[20px]">
					Actually In Their Account
				</p>
				<p className="mt-3 text-[32px] leading-tight font-medium tracking-tight text-[#32302F] 2xl:text-[40px]">
					{formatWholeAmount(averageMonthly)}
					<span className="ml-1 text-[15px] font-normal text-[#747474] 2xl:text-[18px]">
						/month
					</span>
				</p>
				<p className="mt-1 text-[13px] text-[#747474] 2xl:text-[16px]">
					Average Monthly Deposits
				</p>
			</div>
		</div>
	)
}

export function IncomeCategoryPill({
	category,
}: {
	category: string | null | undefined
}) {
	const key = category as IncomeCategoryKey | undefined
	const known = key && key in INCOME_FILTER_LABELS
	const label = known
		? INCOME_FILTER_LABELS[key as IncomeCategoryKey]
		: category?.replace(/_/g, ' ') || 'Other'
	const style = known
		? INCOME_CATEGORY_PILL_STYLES[key as IncomeCategoryKey]
		: { bg: '#F4F6F6', text: '#747474' }
	return (
		<div>
			<span
				className="rounded-full px-2.5 py-0.5 text-[11px] font-medium 2xl:text-[14px]"
				style={{ backgroundColor: style.bg, color: style.text }}
			>
				{label}
			</span>
		</div>
	)
}

export function MonthByMonthList({
	groups,
}: {
	groups: Array<{
		key: string
		label: string
		depositCount: number
		total: number
		items: VerificationDataItem[]
	}>
}) {
	if (groups.length === 0) {
		return (
			<p className="mt-4 text-sm text-[#747474]">
				No income transactions were identified for this applicant.
			</p>
		)
	}
	return (
		<section className="rounded-2xl border border-[#EFEFED] bg-white p-4 sm:p-5">
			<h3 className="text-[15px] font-medium text-[#32302F] 2xl:text-[20px]">
				Month By Month
			</h3>
			<div className="mt-4 space-y-3">
				{groups.map((group) => (
					<div
						key={group.key}
						className="overflow-hidden rounded-xl border border-[#EFEFED] bg-white"
					>
						<div className="flex items-center justify-between gap-3 bg-[#F4F6F6] px-4 py-2.5 text-[13px] text-[#32302F] 2xl:px-5 2xl:py-3.5 2xl:text-[20px]">
							<span>
								<span className="font-medium">{group.label}</span>
								<span className="ml-1.5 text-[13px] font-normal text-[#747474] 2xl:text-[16px]">
									{group.depositCount}{' '}
									{group.depositCount === 1 ? 'deposit' : 'deposits'}
								</span>
							</span>
							<span className="font-medium tabular-nums">
								{formatWholeAmount(group.total)}
							</span>
						</div>
						<ul>
							{group.items.map((item, index) => (
								<li
									key={`${item.date}-${index}`}
									className={cn(
										'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1 px-2 py-3 text-[11px]',
										'sm:flex sm:flex-wrap sm:items-center sm:gap-1 lg:gap-3 lg:px-4 2xl:px-6 2xl:py-3.5 2xl:text-[14px]',
										index > 0 && 'border-t border-dashed border-[#E8E8E6]',
									)}
								>
									<IncomeCategoryPill category={item.category} />
									<span className="col-start-1 row-start-2 min-w-0 text-[#747474] sm:col-auto sm:row-auto sm:flex-1">
										{item.description || '-'}
									</span>
									<span className="col-start-2 row-start-2 text-right text-[#747474] sm:col-auto sm:row-auto sm:text-left">
										{formatTxnDay(item.date)}
									</span>
									<span className="col-start-2 row-start-1 min-w-15 text-right text-[13px] font-semibold text-[#32302F] tabular-nums sm:col-auto sm:row-auto lg:min-w-20 2xl:text-[16px]">
										{formatTxnAmount(item.amount)}
									</span>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
		</section>
	)
}

export function FinancialInstitutionsList({
	accounts,
}: {
	accounts: ConnectedFlinksAccount[]
}) {
	if (accounts.length === 0) return null
	return (
		<section className="mt-5 rounded-2xl border border-[#EFEFED] bg-white p-4 sm:p-5">
			<h3 className="text-[16px] font-semibold text-[#32302F] 2xl:text-[20px]">
				Financial Institution
			</h3>
			<ul className="mt-3 flex flex-col gap-3">
				{accounts.map((account) => (
					<li
						key={account.loginId}
						className="flex items-center gap-3 rounded-2xl border border-[#EFEFED] bg-white px-4 py-3.5 2xl:px-6 2xl:py-4"
					>
						<div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#efeaf6] text-[#9976B8]">
							<Landmark className="size-5" strokeWidth={1.75} />
						</div>
						<div className="min-w-0 flex-1">
							<p className="truncate text-[14px] font-semibold text-[#32302F] 2xl:text-[16px]">
								{account.institution || 'Connected bank'}
							</p>
							<p className="mt-0.5 truncate text-[12px] text-[#747474] 2xl:text-[13px]">
								{formatConnectedDate(account.connectedAt)}
							</p>
							{account.accountHolder ? (
								<p className="mt-1 truncate text-[12px] sm:hidden">
									<span className="text-[#747474]">Account Holder: </span>
									<span className="font-semibold text-[#32302F]">
										{account.accountHolder}
									</span>
								</p>
							) : null}
						</div>
						{account.accountHolder ? (
							<p className="hidden max-w-[40%] shrink-0 truncate text-right text-[13px] sm:block 2xl:text-[14px] 2xl:text-[18px]">
								<span className="text-[#747474]">Account Holder: </span>
								<span className="font-semibold text-[#32302F]">
									{account.accountHolder}
								</span>
							</p>
						) : null}
					</li>
				))}
			</ul>
		</section>
	)
}

export function CollapsibleDisclaimers({
	disclaimers,
}: {
	disclaimers: VerificationDisclaimer[]
}) {
	return (
		<details
			open
			className="group mt-5 rounded-2xl border border-[#EFEFED] bg-white"
		>
			<summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 text-[15px] font-semibold text-[#32302F] 2xl:px-6 2xl:py-5 2xl:text-[20px] [&::-webkit-details-marker]:hidden">
				Disclaimers
				<ChevronDown className="size-4 text-[#747474] transition-transform group-open:rotate-180" />
			</summary>
			<div className="flex flex-col gap-5 px-5 pb-4 2xl:px-6 2xl:pb-5">
				{disclaimers.map((disclaimer) => (
					<div key={disclaimer.heading}>
						<p className="text-[12px] font-semibold text-[#32302F] 2xl:text-[14px]">
							{disclaimer.heading}
						</p>
						<p className="mt-1.5 text-[10px] leading-relaxed text-[#747474] 2xl:text-[13px]">
							{disclaimer.body}
						</p>
					</div>
				))}
			</div>
		</details>
	)
}

/** Centered stat tile; `miniPill` is the rent modal's tiny 'Current' tag. */
export function StatTile({
	tone,
	icon,
	label,
	value,
	miniPill,
	breakdown,
}: {
	tone: PastelTone
	icon: ReactNode
	label: ReactNode
	/** Single value. Ignored when `breakdown` is provided. */
	value?: string
	miniPill?: string
	/** Two-up (or more) amount columns, each with a small caption beneath —
	 * e.g. Monthly / Gross (yearly) on the income tiles. */
	breakdown?: Array<{ amount: string; caption: string }>
}) {
	return (
		<div className="flex flex-col items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-[#fbfdfd] px-2 py-5 text-center">
			<IconSquare tone={tone} className="size-11">
				{icon}
			</IconSquare>
			{miniPill ? (
				<span className="rounded-full bg-[#F1F2F0] px-2.5 py-0.5 text-[11px] text-[#747474]">
					{miniPill}
				</span>
			) : null}
			<span className="text-[14px] text-[#747474]">{label}</span>
			{breakdown ? (
				<div className="-mt-1 flex items-start justify-center gap-7">
					{breakdown.map((column) => (
						<div key={column.caption} className="flex flex-col items-center">
							<span className="text-[21px] leading-6 font-medium text-[#32302F]">
								{column.amount}
							</span>
							<span className="text-[14px] text-[#747474]">
								{column.caption}
							</span>
						</div>
					))}
				</div>
			) : (
				<span className="-mt-2 text-[21px] leading-6 font-medium text-[#32302F]">
					{value}
				</span>
			)}
		</div>
	)
}

/** Section heading with the design's superscript footnote marker. */
export function SectionHeading({ title, sup }: { title: string; sup: string }) {
	return (
		<h3 className="text-[17px] font-semibold text-[#32302F]">
			{title}
			{sup ? (
				<sup className="ml-0.5 font-normal text-gray-500">{sup}</sup>
			) : null}
		</h3>
	)
}

export function TransactionTable({ items }: { items: VerificationDataItem[] }) {
	return (
		<div className="mt-2 overflow-x-auto rounded-2xl border border-[#F0F0EE] px-4 py-3">
			<table className="w-full text-left">
				<thead>
					<tr className="text-[14px] text-[#747474]">
						<th className="w-[20%] pr-3 pb-1 font-normal">Date</th>
						<th className="w-[20%] pr-20 pb-1 text-right font-normal">
							Amount
						</th>
						<th className="w-[60%] pb-1 pl-20 font-normal">Description</th>
					</tr>
				</thead>
				<tbody>
					{items.map((item, index) => (
						<tr
							// Static report rows — index keys are stable here.
							key={`${item.date}-${index}`}
							className="text-[14px] text-[#32302F]"
						>
							<td className="w-[20%] pr-3 pb-1">{formatTxnDate(item.date)}</td>
							<td className="w-[20%] pr-20 pb-1 text-right tabular-nums">
								{formatTxnAmount(item.amount)}
							</td>
							<td className="w-[60%] pb-1 pl-20">{item.description || '-'}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

/** Centered-title month/total card (design §13.3). */
export function MonthlySummaryCard({
	title,
	sup,
	buckets,
}: {
	title: string
	sup: string
	buckets: MonthBucket[]
}) {
	return (
		<div className="rounded-2xl border border-[#F0F0EE] px-5 py-4">
			<p className="text-center text-[14px] text-[#747474]">
				{title}
				{sup ? <sup className="ml-0.5">{sup}</sup> : null}
			</p>
			<div className="mt-2">
				{buckets.map((bucket) => (
					<div
						key={bucket.label}
						className="flex items-center pb-1 text-[14px] text-[#32302F]"
					>
						<span className="flex-1">{bucket.label}</span>

						<span className="ml-auto w-[100px] text-right tabular-nums">
							{formatWholeAmount(bucket.total)}
						</span>
					</div>
				))}
			</div>
		</div>
	)
}

/** Quote-style disclaimer: thick dark-gray rounded bar on the left edge. */
export function DisclaimerBlock({
	disclaimer,
}: {
	disclaimer: VerificationDisclaimer
}) {
	return (
		<div className="rounded-xl border border-l-4 border-[#F0F0F0] border-l-[#747474] py-2.5 pr-4 pl-3">
			<p className="text-[11px] font-semibold text-[#32302F]">
				{disclaimer.heading}
				{disclaimer.sup ? (
					<sup className="relative top-[1px] left-[2px] align-super text-[9px] text-[#747474]">
						{disclaimer.sup}
					</sup>
				) : null}
			</p>
			{disclaimer.subHeading ? (
				<p className="mt-0.5 text-[10px] font-medium text-[#747474]">
					{disclaimer.subHeading}
				</p>
			) : null}
			<p className="mt-1 text-[11px] leading-relaxed text-[#747474]">
				{disclaimer.body}
			</p>
		</div>
	)
}

export function VerificationModalFooter({
	showRequestBank,
	onRequestBank,
	onDownload,
	downloadBusy,
	showDownload,
}: {
	showRequestBank: boolean
	onRequestBank: () => void
	onDownload: () => void
	downloadBusy: boolean
	showDownload: boolean
}) {
	return (
		<div
			className={cn(
				'mt-6 flex flex-wrap items-center gap-3',
				showRequestBank ? 'justify-between' : 'justify-end',
			)}
		>
			{showRequestBank ? (
				<button
					type="button"
					onClick={onRequestBank}
					className="flex h-10 cursor-pointer items-center gap-2 rounded-full border border-[#C9C9C7] bg-white px-6 text-[15px] font-medium text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
				>
					Request Additional Bank Account
					<CreditCard className="size-4" />
				</button>
			) : null}
			{showDownload ? (
				<button
					type="button"
					onClick={onDownload}
					disabled={downloadBusy}
					className="flex h-10 cursor-pointer items-center gap-2 rounded-full bg-[#32302F] px-6 text-[15px] font-medium text-white transition-colors hover:bg-[#1a1918] disabled:cursor-not-allowed disabled:opacity-60"
				>
					<Download className="size-4" />
					{downloadBusy ? 'Preparing...' : 'Download'}
				</button>
			) : null}
		</div>
	)
}
